import { create } from "zustand"

import type {
  GetPriceChangeHistoryQuery,
  PriceChangeHistorySortField,
  PriceChangeSubmission,
  PriceChangeSubmissionStatus,
  SortingOrder,
} from "@/graphql/codegen/graphql"
import { GetPriceChangeHistoryDocument, PriceChangeType } from "@/graphql/codegen/graphql"
import { type SearchFilters, createSearchStore } from "@/stores/createSearchStore"

export type PriceChangeSearchFilters = SearchFilters & {
  field?: PriceChangeHistorySortField
  order?: SortingOrder
  status?: PriceChangeSubmissionStatus[]
  changeType: PriceChangeType
  dateFrom?: string
  dateTo?: string
  assignedToIds?: string[]
}

const deduplicateData = (
  newData: PriceChangeSubmission[],
  existingData: PriceChangeSubmission[]
): PriceChangeSubmission[] => {
  const uniqueIds = new Set(existingData.map((priceChangeSubmission) => priceChangeSubmission.id))
  return [...existingData, ...newData.filter((priceChangeSubmission) => !uniqueIds.has(priceChangeSubmission.id))]
}

export const usePriceChangeHistorySearchStore = create(
  createSearchStore<PriceChangeSubmission, PriceChangeSearchFilters, GetPriceChangeHistoryQuery>({
    documentNode: GetPriceChangeHistoryDocument,
    deduplicateData,
    getQueryInput: (filters: PriceChangeSearchFilters, offset: number, limit: number) => {
      const { field, order, query, dateFrom, dateTo, changeType, ...otherFilters } = filters
      return {
        filter: {
          ...otherFilters,
          ...(dateFrom || dateTo
            ? {
                dateRange: {
                  from: dateFrom,
                  to: dateTo,
                },
              }
            : {}),
          changeType: changeType ?? PriceChangeType.SellingPrice,
          search: query,
        },
        offset,
        limit,
        sort: { field, direction: order },
      }
    },
    initialState: {
      limit: 30,
    },
    getQueryVariables: (queryInput) => ({ input: queryInput }),
    extractDataFromResponse: (data) => ({
      __typename: data.priceChange.getPriceChangeHistory.__typename,
      data:
        data?.priceChange.getPriceChangeHistory.__typename === "GetPriceChangeHistorySuccess"
          ? (data.priceChange.getPriceChangeHistory.priceChangeSubmissions as PriceChangeSubmission[])
          : [],
      error:
        data?.priceChange.getPriceChangeHistory.__typename === "GetPriceChangeHistoryFailure"
          ? data.priceChange.getPriceChangeHistory.error
          : undefined,
    }),
  })
)
